// Vendors
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useErrorBoundary } from 'react-error-boundary';
// Constants
import {
  OTP_DEFAULT_PROPS,
  OTP_RETRY_TIMEOUT_KEY,
  VERYFY_DEFAULT_PROPS,
} from '../constants/contract-otp.constants';
// Contexts
import { useLayoutContextConsumerHook } from 'components/layout/contexts/layout.context';
import { useAppContextConsumerHook } from 'contexts/app.context';
// Handlers
import { ContractOtpHandlers } from '../handlers/contract-otp.handlers';
import { ContractOtpTrackingHandlers } from '../handlers/contract-otp.tracking.handlers';
// Hooks
import { useCancelDropModalHook } from 'components/cancel-drop-modal/components/footer/hooks/cancel-drop-modal-footer.hook';
import { useAppTrackingHook } from 'tracking/app-tracking.hook';
// Types
import { OtpInfoType, VerifyInfoType } from '../handlers/types/contract-otp.handlers.type';
import { ContractOtpHookResultType } from './types/contract-otp.hook.type';
// Utils
import { getOtpDelay } from '../handlers/utils/contract-otp.handlers.utils';
import { formatHashedPhone } from '../utils/contract-otp.utils';

const useContractOtpHook = (): ContractOtpHookResultType => {
  const { formatMessage } = useIntl();

  const [lastOtpRequestTimestamp, setLastOtpRequestTimestamp] = useState(
    localStorage.getItem(OTP_RETRY_TIMEOUT_KEY)
  );

  const [otpInfo, setOtpInfo] = useState<OtpInfoType>({
    ...OTP_DEFAULT_PROPS,
    delay: getOtpDelay(),
  });

  const [verifyInfo, setVerifyInfo] = useState<VerifyInfoType>(VERYFY_DEFAULT_PROPS);
  const [phone, setPhone] = useState('');

  const { callbackUrl, loanId, setCurrentState } = useAppContextConsumerHook();

  const { setMainButtonConfiguration } = useLayoutContextConsumerHook();

  const { handleConfirmDropOnboardingProcessButtonClick } = useCancelDropModalHook();

  const { showBoundary: setError } = useErrorBoundary();

  const {
    handleClickResendOtpTracking,
    handleContinueButtonClickTracking,
    handleContractOtpPageDisplayedTracking,
    handleErrorMessageDisplayedTracking,
    handleOtpStartTracking,
    handleTextboxClickTracking,
  } = useAppTrackingHook(ContractOtpTrackingHandlers);

  const {
    handleCancelProcess,
    handleChangeOtpCode,
    handleClearOtp,
    handleEnableNewOtpButton,
    handleGetOtpInfo,
    handleOpenCloseSnackbar,
    handleResendOtp,
    handleUpdateButtonConfiguration,
    handleVerifyOtp,
  } = ContractOtpHandlers({
    callbackUrl,
    formatMessage,
    handleClickResendOtpTracking,
    handleConfirmDropOnboardingProcessButtonClick,
    handleContinueButtonClickTracking,
    handleErrorMessageDisplayedTracking,
    loanId,
    otpInfo,
    setCurrentState,
    setError,
    setLastOtpRequestTimestamp,
    setMainButtonConfiguration,
    setOtpInfo,
    setPhone,
    setVerifyInfo,
    verifyInfo,
  });

  useEffect(() => {
    handleUpdateButtonConfiguration();
  }, [otpInfo?.code, verifyInfo?.attempts, verifyInfo?.fetching]);

  useEffect(() => {
    handleEnableNewOtpButton();
  }, [lastOtpRequestTimestamp, otpInfo.attempts]);

  useEffect(() => {
    handleGetOtpInfo();
    handleOtpStartTracking();
    handleContractOtpPageDisplayedTracking();
  }, []);

  useEffect(() => {
    handleCancelProcess();
  }, [otpInfo.attempts, verifyInfo.attempts]);

  return {
    formatMessage,
    handleChangeOtpCode,
    handleClearOtp,
    handleOpenCloseSnackbar,
    handleResendOtp,
    handleVerifyOtp,
    handleErrorMessageDisplayedTracking,
    handleTextboxClickTracking,
    otpInfo,
    setOtpInfo,
    verifyInfo,
    hashedPhone: formatHashedPhone(phone),
  };
};

export { useContractOtpHook };
